import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { StatCard } from '@/components/ui/stat-card';
import { Select, Input } from '@/components/ui/input';
import { useThemeStore } from '@/store/themeStore';
import { useFisheriesStore } from '@/store/fisheriesStore';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import {
    Anchor, TrendingUp, TrendingDown, Activity, BarChart2, RefreshCw,
    AlertTriangle, CheckCircle, Loader2, Fish, Calendar, MapPin, Info,
    Database, Sparkles, Search, ChevronDown, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';

import { apiClient } from '@/services/api';

interface ApiResponseBase {
    success: boolean;
    error?: string;
}

interface CPUEValue {
    cpue?: number;
    totalCatch?: number;
    totalEffort?: number;
    trend?: 'increasing' | 'decreasing' | 'stable' | string;
    confidence95?: {
        lower?: number;
        upper?: number;
    };
}

interface CPUETimePoint {
    period: string;
    cpue?: number;
    catch?: number;
    effort?: number;
    sampleSize?: number;
}

interface YearlyCPUEPoint {
    year: number;
    catch?: number;
    effort?: number;
    cpue?: number;
}

interface StockStatus {
    biomassStatus?: string;
    exploitationLevel?: string;
    sustainabilityScore?: number;
    recommendations?: string[];
}

interface Mortality {
    totalMortality?: number;
    naturalMortality?: number;
    naturalMortalityEstimates?: {
        pauly?: number;
    };
    fishingMortality?: number;
    exploitationRate?: number;
    F?: number;
    M?: number;
}

interface Recruitment {
    pattern?: string;
    index?: number;
}

interface LengthDistribution {
    bins?: Array<{ lengthClass: number; count: number }>;
    sampleSize?: number;
    meanLength?: number;
    minLength?: number;
    maxLength?: number;
    mode?: number;
    medianLength?: number;
    standardDeviation?: number;
}

interface GrowthParams {
    Linf?: number;
    K?: number;
    r2?: number;
}

interface LengthAnalysis {
    distribution?: LengthDistribution;
    cohorts?: unknown;
    growthParams?: GrowthParams;
    lengthWeight?: unknown;
    maturityOgive?: Array<{ lengthClass: number; percentMature: number }>;
}

interface AnalyzeStockBundle {
    stockStatus?: StockStatus;
    mortality?: Mortality;
    recruitment?: Recruitment;
    fmRatio?: number | null;
    sustainabilityStatus?: 'sustainable' | 'fully_exploited' | 'overfished' | string;
}

interface DatasetsResponse extends ApiResponseBase {
    datasets?: Array<{ species?: string[] }>;
    stats?: {
        totalCatchRecords?: number;
        totalLengthRecords?: number;
        species?: string[];
    };
}

interface AnalyzeWithDataResponse extends ApiResponseBase {
    recordsUsed?: { catch?: number; length?: number };
    cpue?: CPUEValue;
    timeSeries?: CPUETimePoint[];
    yearlyTimeSeries?: YearlyCPUEPoint[];
    dateRange?: { startYear?: number; endYear?: number };
    length?: LengthAnalysis | null;
    stock?: AnalyzeStockBundle | null;
    fmRatio?: number | null;
    sustainabilityStatus?: 'sustainable' | 'fully_exploited' | 'overfished' | string;
}

interface StockAssessmentResponse extends ApiResponseBase {
    stockStatus?: StockStatus;
    mortality?: Mortality;
    recruitment?: Recruitment;
}

interface CPUEAnalysisResponse extends ApiResponseBase {
    cpue?: CPUEValue;
    timeSeries?: CPUETimePoint[];
}

interface DepthDistributionResponse extends ApiResponseBase {
    data?: Array<{
        depthRange: string;
        depthMid: number;
        totalCatch: number;
        sampleCount: number;
        avgCatch: number;
    }>;
    summary?: {
        totalSamples?: number;
        depthRange?: { min: number; max: number } | null;
        peakDepth?: { depthRange: string; totalCatch: number } | null;
    };
}

// API service for fisheries
const fisheriesService = {
    getInfo: async () => {
        return apiClient.get<ApiResponseBase>('/fisheries/info');
    },
    calculateCPUE: async (data: unknown) => {
        return apiClient.post<CPUEAnalysisResponse>('/fisheries/cpue', data);
    },
    analyzeLengthFrequency: async (data: unknown) => {
        return apiClient.post<ApiResponseBase>('/fisheries/length-analysis', data);
    },
    assessStock: async (data: unknown) => {
        return apiClient.post<StockAssessmentResponse>('/fisheries/stock-assessment', data);
    },
    // NEW: Get uploaded datasets
    getDatasets: async () => {
        return apiClient.get<DatasetsResponse>('/fisheries/datasets');
    },
    // NEW: Analyze using uploaded data
    analyzeWithData: async (species: string, datasetId?: string) => {
        return apiClient.post<AnalyzeWithDataResponse>('/fisheries/analyze-with-data', { species, datasetId });
    },
    // Spatial CPUE for heatmap
    getSpatialCpue: async (species?: string) => {
        return apiClient.get('/fisheries/spatial-cpue', species ? { species } : undefined);
    },
    // Depth distribution
    getDepthDistribution: async (species?: string) => {
        return apiClient.get<DepthDistributionResponse>('/fisheries/depth-distribution', species ? { species } : undefined);
    },
};

const CHART_COLORS = ['#0891b2', '#10b981', '#f97316', '#8b5cf6', '#ec4899'];

export default function FisheriesAnalytics() {
    const navigate = useNavigate();
    const { resolvedTheme } = useThemeStore();
    const isDark = resolvedTheme === 'dark';
    const [activeTab, setActiveTab] = useState<'overview' | 'cpue' | 'length' | 'stock'>('overview');
    const [useUploadedData, setUseUploadedData] = useState(true); // Default to uploaded data
    const [showSpeciesDropdown, setShowSpeciesDropdown] = useState(false);
    const speciesInputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Session store for persistence across tab switches - stores entire raw API response
    const { selectedSpecies, setSelectedSpecies, analysisData, setAnalysisData } = useFisheriesStore();

    // Use store species as input (synced)
    const speciesInput = selectedSpecies;
    const setSpeciesInput = setSelectedSpecies;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                speciesInputRef.current &&
                !speciesInputRef.current.contains(event.target as Node)
            ) {
                setShowSpeciesDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Chart colors based on theme
    const chartColors = {
        grid: isDark ? '#374151' : '#e5e7eb',
        text: isDark ? '#9ca3af' : '#64748b',
        tooltipBg: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        tooltipText: isDark ? '#f3f4f6' : '#1e293b',
    };

    // Fetch module info
    const { data: moduleInfo } = useQuery({
        queryKey: ['fisheries-info'],
        queryFn: fisheriesService.getInfo,
    });

    // CPUE calculation mutation
    const cpueMutation = useMutation({
        mutationFn: fisheriesService.calculateCPUE,
        onSuccess: (data) => {
            if (data.success) {
                toast.success('CPUE analysis completed');
            } else {
                toast.error(data.error || 'Analysis failed');
            }
        },
        onError: () => toast.error('Failed to connect to API'),
    });

    // Stock assessment mutation
    const stockMutation = useMutation({
        mutationFn: fisheriesService.assessStock,
        onSuccess: (data) => {
            if (data.success) {
                toast.success('Stock assessment completed');
            } else {
                toast.error(data.error || 'Assessment failed');
            }
        },
        onError: () => toast.error('Failed to connect to API'),
    });

    // NEW: Fetch uploaded datasets
    const { data: datasetsData } = useQuery({
        queryKey: ['fisheries-datasets'],
        queryFn: fisheriesService.getDatasets,
        refetchOnWindowFocus: false,
    });

    // NEW: Analyze with uploaded data mutation
    const analyzeWithDataMutation = useMutation({
        mutationFn: ({ species }: { species: string }) =>
            fisheriesService.analyzeWithData(species),
        onSuccess: (data) => {
            if (data.success) {
                toast.success(`Analysis complete using ${data.recordsUsed?.catch || 0} uploaded records`);
                // Save entire raw API response to session store
                setAnalysisData(data);
            } else {
                toast.error(data.error || 'No data found for this species');
            }
        },
        onError: () => toast.error('Failed to analyze data'),
    });

    // Depth distribution query (for uploaded data with depth info)
    const { data: depthDistData } = useQuery({
        queryKey: ['depth-distribution', speciesInput],
        queryFn: () => fisheriesService.getDepthDistribution(speciesInput),
        enabled: useUploadedData && speciesInput.length > 0,
        staleTime: 1000 * 60 * 5,
    });

    // Run CPUE analysis - uses uploaded data if available
    const runCPUEAnalysis = () => {
        if (!speciesInput.trim()) {
            toast.error('Please enter a species name first');
            return;
        }

        if (useUploadedData) {
            // Use uploaded data from database
            analyzeWithDataMutation.mutate({ species: speciesInput });
        } else {
            // Use sample data (for demo)
            cpueMutation.mutate({
                species: speciesInput,
                records: [
                    { date: '2024-01-15', catch: 1520, effort: 100, species: speciesInput },
                    { date: '2024-02-10', catch: 1680, effort: 100, species: speciesInput },
                    { date: '2024-03-20', catch: 1850, effort: 100, species: speciesInput },
                    { date: '2024-04-05', catch: 2210, effort: 100, species: speciesInput },
                    { date: '2024-05-12', catch: 2560, effort: 100, species: speciesInput },
                    { date: '2024-06-18', catch: 2340, effort: 100, species: speciesInput },
                ],
            });
        }
    };

    // Run stock assessment
    const runStockAssessment = () => {
        if (!speciesInput.trim()) {
            toast.error('Please enter a species name first');
            return;
        }

        if (useUploadedData && analyzeWithDataMutation.data?.stock) {
            // Already have stock data from analyzeWithData
            toast.success('Stock data available from uploaded records');
        } else {
            // Use sample data
            stockMutation.mutate({
                species: speciesInput,
                catchRecords: [
                    { date: '2024-01-15', catch: 500, species: speciesInput },
                    { date: '2024-02-10', catch: 800, species: speciesInput },
                    { date: '2024-03-20', catch: 600, species: speciesInput },
                    { date: '2024-04-05', catch: 350, species: speciesInput },
                    { date: '2024-05-12', catch: 150, species: speciesInput },
                ],
                lengthRecords: [
                    { length: 45, species: speciesInput },
                    { length: 52, species: speciesInput },
                    { length: 38, species: speciesInput },
                    { length: 60, species: speciesInput },
                ],
                options: { averageTemperature: 28.5 },
            });
        }
    };

    // Prepare chart data from API results
    // Use session store analysisData (persisted) when available, otherwise use mutation data
    const storedData = analysisData || analyzeWithDataMutation.data;

    // CPUE data
    const cpueData = useUploadedData && storedData?.cpue ? storedData.cpue : cpueMutation.data?.cpue;
    const cpueTimeSeries = useUploadedData && storedData?.timeSeries ? storedData.timeSeries : (cpueMutation.data?.timeSeries || []);
    const yearlyTimeSeries = useUploadedData && storedData?.yearlyTimeSeries ? storedData.yearlyTimeSeries : [];
    const dateRange = storedData?.dateRange;

    // Stock data - API returns 'stock' object containing stockStatus, mortality, etc.
    const stockData = storedData?.stock;
    const stockStatus = useUploadedData && stockData?.stockStatus
        ? stockData.stockStatus
        : stockMutation.data?.stockStatus;
    const mortality = useUploadedData && stockData?.mortality
        ? stockData.mortality
        : stockMutation.data?.mortality;
    const recruitment = useUploadedData && stockData?.recruitment
        ? stockData.recruitment
        : stockMutation.data?.recruitment;
    const fmRatio = stockData?.fmRatio ?? storedData?.fmRatio ?? null;
    const sustainabilityStatus = stockData?.sustainabilityStatus ?? storedData?.sustainabilityStatus ?? null;

    // Length frequency data
    const lengthData = storedData?.length;
    const lengthDistribution = useUploadedData && lengthData?.distribution ? lengthData.distribution : null;
    const cohorts = useUploadedData && lengthData?.cohorts ? lengthData.cohorts : null;
    const growthParams = useUploadedData && lengthData?.growthParams ? lengthData.growthParams : null;
    const lengthWeight = useUploadedData && lengthData?.lengthWeight ? lengthData.lengthWeight : null;
    const maturityOgive = useUploadedData && lengthData?.maturityOgive ? lengthData.maturityOgive : null;

    // Data source info for UI
    const localDataStats = datasetsData?.stats;

    const availableSpecies: string[] = (() => {
        const fromDatasets = (datasetsData?.datasets || [])
            .flatMap((dataset: any) => Array.isArray(dataset?.species) ? dataset.species : [])
            .filter((species: unknown): species is string => typeof species === 'string' && species.trim().length > 0);

        if (fromDatasets.length > 0) {
            return Array.from(new Set(fromDatasets)).sort((a, b) => a.localeCompare(b));
        }

        const fromStats = Array.isArray(localDataStats?.species) ? localDataStats.species : [];
        return fromStats;
    })();
    const isAnalyzing = cpueMutation.isPending || stockMutation.isPending || analyzeWithDataMutation.isPending;

    // Filter species list based on input (for searchable dropdown)
    const filteredSpecies = speciesInput.trim()
        ? availableSpecies.filter((s: string) =>
            s.toLowerCase().includes(speciesInput.toLowerCase())
        )
        : availableSpecies;

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Anchor className="w-5 h-5 text-ocean-500" />
                        <span className="text-sm font-medium text-ocean-600 dark:text-ocean-400">Fisheries Module</span>
                    </div>
                    <h1 className="text-3xl lg:text-4xl font-bold text-deep-900 dark:text-gray-100">
                        Stock & Abundance Analysis
                    </h1>
                    <p className="text-deep-500 dark:text-gray-400 mt-1">
                        CPUE calculations, length-frequency analysis, and stock assessments
                    </p>
                </div>
                <div className="flex gap-3">
                    <Button
                        variant="outline"
                        onClick={runCPUEAnalysis}
                        disabled={cpueMutation.isPending || analyzeWithDataMutation.isPending}
                    >
                        {(cpueMutation.isPending || analyzeWithDataMutation.isPending)
                            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : <RefreshCw className="w-4 h-4 mr-2" />}
                        Analyze CPUE
                    </Button>
                    <Button
                        variant="premium"
                        onClick={runStockAssessment}
                        disabled={stockMutation.isPending}
                    >
                        {stockMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Fish className="w-4 h-4 mr-2" />}
                        Assess Stock
                    </Button>
                </div>
            </div>

            {/* Species Input */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex gap-4 items-start flex-wrap">
                        <div className="flex-1 min-w-[200px] relative">
                            <label className="block text-sm font-medium text-deep-700 dark:text-gray-300 mb-2">
                                Target Species
                            </label>
                            <div className="relative">
                                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-deep-400 dark:text-gray-500">
                                    <Search className="w-4 h-4" />
                                </div>
                                <input
                                    ref={speciesInputRef}
                                    type="text"
                                    value={speciesInput}
                                    onChange={(e) => {
                                        setSpeciesInput(e.target.value);
                                        setShowSpeciesDropdown(true);
                                    }}
                                    onFocus={() => setShowSpeciesDropdown(true)}
                                    placeholder="Search species or type a name..."
                                    className={cn(
                                        "w-full h-10 pl-9 pr-16 text-sm rounded-lg border transition-all",
                                        "bg-white dark:bg-gray-900 text-deep-900 dark:text-gray-100",
                                        "placeholder:text-deep-400 dark:placeholder:text-gray-500",
                                        showSpeciesDropdown
                                            ? "border-ocean-400 ring-2 ring-ocean-100 dark:ring-ocean-900/30"
                                            : "border-gray-200 dark:border-gray-700 hover:border-gray-300",
                                        "focus:outline-none focus:border-ocean-400 focus:ring-2 focus:ring-ocean-100 dark:focus:ring-ocean-900/30"
                                    )}
                                />
                                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                    {speciesInput && (
                                        <button
                                            onClick={() => { setSpeciesInput(''); speciesInputRef.current?.focus(); }}
                                            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-deep-400 dark:text-gray-500"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setShowSpeciesDropdown(!showSpeciesDropdown)}
                                        className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-deep-400 dark:text-gray-500"
                                    >
                                        <ChevronDown className={cn("w-4 h-4 transition-transform", showSpeciesDropdown && "rotate-180")} />
                                    </button>
                                </div>
                            </div>

                            {/* Species Dropdown Overlay */}
                            {showSpeciesDropdown && (
                                <div
                                    ref={dropdownRef}
                                    className={cn(
                                        "absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border shadow-xl",
                                        "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700",
                                        "animate-in fade-in slide-in-from-top-2 duration-200"
                                    )}
                                >
                                    {availableSpecies.length === 0 ? (
                                        <div className="px-4 py-6 text-center">
                                            <Fish className="w-8 h-8 mx-auto mb-2 text-ocean-300" />
                                            <p className="text-sm text-deep-500 dark:text-gray-400">No species in database</p>
                                            <p className="text-xs text-deep-400 dark:text-gray-500 mt-1">
                                                Upload fisheries data via <a href="/ingestion" className="underline text-ocean-500">Data Ingestion</a>
                                            </p>
                                        </div>
                                    ) : filteredSpecies.length === 0 ? (
                                        <div className="px-4 py-4 text-center">
                                            <p className="text-sm text-deep-500 dark:text-gray-400">No matching species</p>
                                            <p className="text-xs text-deep-400 dark:text-gray-500 mt-1">
                                                Press Enter to use "{speciesInput}" anyway
                                            </p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
                                                <p className="text-[10px] font-medium text-deep-400 dark:text-gray-500 uppercase tracking-wider">
                                                    {filteredSpecies.length} species available
                                                </p>
                                            </div>
                                            {filteredSpecies.map((species: string) => {
                                                const isSelected = speciesInput.toLowerCase() === species.toLowerCase();
                                                return (
                                                    <button
                                                        key={species}
                                                        onClick={() => {
                                                            setSpeciesInput(species);
                                                            setShowSpeciesDropdown(false);
                                                        }}
                                                        className={cn(
                                                            "w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors",
                                                            isSelected
                                                                ? "bg-ocean-50 dark:bg-ocean-900/20 text-ocean-700 dark:text-ocean-300"
                                                                : "hover:bg-gray-50 dark:hover:bg-gray-800/50 text-deep-800 dark:text-gray-200"
                                                        )}
                                                    >
                                                        <Fish className={cn(
                                                            "w-4 h-4 flex-shrink-0",
                                                            isSelected ? "text-ocean-500" : "text-deep-300 dark:text-gray-600"
                                                        )} />
                                                        <span className="flex-1">
                                                            {speciesInput.trim() ? (
                                                                // Highlight matching text
                                                                (() => {
                                                                    const idx = species.toLowerCase().indexOf(speciesInput.toLowerCase());
                                                                    if (idx === -1) return <em className="italic">{species}</em>;
                                                                    return (
                                                                        <>
                                                                            {species.slice(0, idx)}
                                                                            <span className="font-semibold text-ocean-600 dark:text-ocean-400 bg-ocean-50 dark:bg-ocean-900/30 rounded px-0.5">
                                                                                {species.slice(idx, idx + speciesInput.length)}
                                                                            </span>
                                                                            {species.slice(idx + speciesInput.length)}
                                                                        </>
                                                                    );
                                                                })()
                                                            ) : (
                                                                <em className="italic">{species}</em>
                                                            )}
                                                        </span>
                                                        {isSelected && (
                                                            <CheckCircle className="w-4 h-4 text-ocean-500 flex-shrink-0" />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Data Stats or Upload Prompt */}
                        <div className={cn(
                            "flex-1 min-w-[280px] p-3 rounded-lg border",
                            useUploadedData && localDataStats?.totalCatchRecords > 0
                                ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                                : useUploadedData
                                    ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800"
                                    : "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                        )}>
                            {useUploadedData ? (
                                localDataStats?.totalCatchRecords > 0 ? (
                                    <div className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-green-700 dark:text-green-300">
                                            <p className="font-medium">Real Data Available</p>
                                            <p className="text-xs">{localDataStats.totalCatchRecords} catch records • {localDataStats.totalLengthRecords} length records • {availableSpecies.length} species</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm text-yellow-700 dark:text-yellow-300">
                                            <p className="font-medium">No Data Uploaded</p>
                                            <p className="text-xs">Go to <a href="/ingestion" className="underline font-medium">Data Ingestion</a> and upload "Fisheries Catch Data" CSV</p>
                                        </div>
                                    </div>
                                )
                            ) : (
                                <div className="flex items-start gap-2">
                                    <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-blue-700 dark:text-blue-300">
                                        <p className="font-medium">Demo Mode</p>
                                        <p className="text-xs">Using simulated sample data for demonstration</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <div className="flex gap-2 flex-wrap">
                {['overview', 'cpue', 'length', 'stock'].map(tab => (
                    <Button
                        key={tab}
                        variant={activeTab === tab ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setActiveTab(tab as any)}
                        className="capitalize"
                    >
                        {tab === 'cpue' ? 'CPUE Analysis' : tab}
                    </Button>
                ))}
            </div>

            {/* Data Source Label Banner */}
            {useUploadedData && dateRange && (
                <div className="flex items-center gap-2 text-sm bg-ocean-50 dark:bg-ocean-900/20 border border-ocean-200 dark:border-ocean-800 rounded-lg px-4 py-2">
                    <Database className="w-4 h-4 text-ocean-600 dark:text-ocean-400" />
                    <span className="text-ocean-700 dark:text-ocean-300">
                        Based on uploaded survey data ({dateRange?.startYear || '—'}–{dateRange?.endYear || '—'})
                    </span>
                    <Badge variant="outline" className="ml-auto">
                        {analyzeWithDataMutation.data?.recordsUsed?.catch?.toLocaleString() || 0} catch records
                    </Badge>
                </div>
            )}

            {activeTab === 'overview' && (
                <>
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            title="Average CPUE"
                            value={cpueData?.cpue?.toFixed(1) || '—'}
                            change={cpueData ? (cpueData.trend === 'increasing' ? 8.5 : cpueData.trend === 'decreasing' ? -5.2 : 0) : undefined}
                            changeLabel="kg/hour"
                            icon={<BarChart2 className="w-5 h-5" />}
                            iconColor="ocean"
                        />
                        <StatCard
                            title="Total Catch"
                            value={cpueData?.totalCatch?.toLocaleString() || '—'}
                            change={cpueData?.totalCatch ? undefined : undefined}
                            changeLabel="this period"
                            icon={<Fish className="w-5 h-5" />}
                            iconColor="marine"
                        />
                        <StatCard
                            title="Fishing Effort"
                            value={cpueData?.totalEffort?.toLocaleString() || '—'}
                            change={cpueData?.totalEffort ? undefined : undefined}
                            changeLabel="tows"
                            icon={<Calendar className="w-5 h-5" />}
                            iconColor="coral"
                        />
                        <StatCard
                            title="Sustainability"
                            value={stockStatus?.sustainabilityScore?.toFixed(0) || '—'}
                            change={stockStatus?.sustainabilityScore ? (stockStatus.sustainabilityScore >= 70 ? 5 : stockStatus.sustainabilityScore < 40 ? -15 : -5) : undefined}
                            changeLabel="score"
                            icon={stockStatus?.sustainabilityScore >= 70 ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                            iconColor={stockStatus?.sustainabilityScore >= 70 ? 'marine' : 'coral'}
                        />
                    </div>

                    {/* Results Display */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* CPUE Results */}
                        <Card>
                            <CardHeader>
                                <CardTitle>CPUE Analysis Results</CardTitle>
                                <CardDescription>Latest catch per unit effort calculations</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {(cpueMutation.isPending || analyzeWithDataMutation.isPending) ? (
                                    <div className="h-60 flex items-center justify-center">
                                        <Loader2 className="w-8 h-8 animate-spin text-ocean-500" />
                                    </div>
                                ) : cpueData ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 bg-ocean-50 dark:bg-ocean-900/20 rounded-xl">
                                                <div className="text-sm text-ocean-600 dark:text-ocean-400">CPUE</div>
                                                <div className="text-2xl font-bold text-deep-900 dark:text-gray-100">
                                                    {cpueData.cpue?.toFixed(2)} kg/hr
                                                </div>
                                            </div>
                                            <div className="p-4 bg-marine-50 dark:bg-marine-900/20 rounded-xl">
                                                <div className="text-sm text-marine-600 dark:text-marine-400">Trend</div>
                                                <div className="text-2xl font-bold text-deep-900 dark:text-gray-100 flex items-center gap-2">
                                                    {cpueData.trend === 'increasing' ? (
                                                        <><TrendingUp className="text-green-500" /> Increasing</>
                                                    ) : cpueData.trend === 'decreasing' ? (
                                                        <><TrendingDown className="text-red-500" /> Decreasing</>
                                                    ) : (
                                                        <><Activity className="text-blue-500" /> Stable</>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {cpueData.confidence95 && (
                                            <div className="p-3 bg-gray-50 dark:bg-deep-800 rounded-lg">
                                                <span className="text-sm text-deep-600 dark:text-gray-300">
                                                    95% CI: {cpueData.confidence95.lower?.toFixed(2)} - {cpueData.confidence95.upper?.toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="h-60 flex flex-col items-center justify-center text-deep-500 dark:text-gray-400">
                                        <Fish className="w-12 h-12 mb-4 text-ocean-300" />
                                        <p>Click "Analyze CPUE" to run analysis</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Stock Assessment */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Stock Assessment</CardTitle>
                                <CardDescription>Mortality and sustainability analysis</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {stockMutation.isPending ? (
                                    <div className="h-60 flex items-center justify-center">
                                        <Loader2 className="w-8 h-8 animate-spin text-ocean-500" />
                                    </div>
                                ) : stockStatus ? (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 bg-gray-50 dark:bg-deep-800 rounded-xl">
                                                <div className="text-sm text-deep-500 dark:text-gray-400">Biomass Status</div>
                                                <div className="text-lg font-bold text-deep-900 dark:text-gray-100">{stockStatus.biomassStatus}</div>
                                            </div>
                                            <div className="p-4 bg-gray-50 dark:bg-deep-800 rounded-xl">
                                                <div className="text-sm text-deep-500 dark:text-gray-400">Exploitation</div>
                                                <div className="text-lg font-bold text-deep-900 dark:text-gray-100">{stockStatus.exploitationLevel}</div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between mb-2">
                                                <span className="text-sm text-deep-600 dark:text-gray-300">Sustainability Score</span>
                                                <span className="text-sm font-medium text-deep-900 dark:text-gray-100">{stockStatus.sustainabilityScore}%</span>
                                            </div>
                                            <Progress value={stockStatus.sustainabilityScore} variant="gradient" />
                                        </div>
                                        {stockStatus.recommendations?.length > 0 && (
                                            <div className="space-y-2">
                                                <div className="text-sm font-medium text-deep-700 dark:text-gray-300">Recommendations:</div>
                                                {stockStatus.recommendations.slice(0, 3).map((rec: string, i: number) => (
                                                    <div key={i} className="flex items-start gap-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm">
                                                        <Info className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                                                        <span className="text-yellow-800 dark:text-yellow-200">{rec}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="h-60 flex flex-col items-center justify-center text-deep-500 dark:text-gray-400">
                                        <BarChart2 className="w-12 h-12 mb-4 text-ocean-300" />
                                        <p>Click "Assess Stock" to run assessment</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </>
            )}

            {activeTab === 'cpue' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Yearly CPUE Bar Chart */}
                    <Card>
                        <CardHeader>
                            <CardTitle>CPUE by Year</CardTitle>
                            <CardDescription>Annual catch per unit effort trend</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {yearlyTimeSeries.length > 0 ? (
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={yearlyTimeSeries}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                        <XAxis dataKey="year" stroke={chartColors.text} />
                                        <YAxis stroke={chartColors.text} label={{ value: 'CPUE (kg/tow)', angle: -90, position: 'insideLeft', fill: chartColors.text }} />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: chartColors.tooltipBg, color: chartColors.tooltipText, border: 'none', borderRadius: '12px' }}
                                            formatter={(value: any, name: string) => {
                                                if (name === 'cpue') return [`${value} kg/tow`, 'CPUE'];
                                                if (name === 'catch') return [`${value} kg`, 'Total Catch'];
                                                if (name === 'effort') return [`${value} tows`, 'Effort'];
                                                return [value, name];
                                            }}
                                        />
                                        <Bar dataKey="cpue" fill="#0891b2" name="cpue" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-80 flex flex-col items-center justify-center text-deep-500 dark:text-gray-400">
                                    <BarChart2 className="w-12 h-12 mb-4 text-ocean-300" />
                                    <p>Run CPUE analysis to see yearly trend</p>
                                    <Button variant="outline" className="mt-4" onClick={runCPUEAnalysis}>
                                        Analyze CPUE
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Monthly CPUE Line Chart */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Monthly Time Series</CardTitle>
                            <CardDescription>Catch per unit effort by month</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {cpueTimeSeries.length > 0 ? (
                                <ResponsiveContainer width="100%" height={350}>
                                    <LineChart data={cpueTimeSeries}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                        <XAxis dataKey="period" stroke={chartColors.text} />
                                        <YAxis stroke={chartColors.text} />
                                        <Tooltip contentStyle={{ backgroundColor: chartColors.tooltipBg, color: chartColors.tooltipText, border: 'none', borderRadius: '12px' }} />
                                        <Line type="monotone" dataKey="cpue" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} name="CPUE" />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-80 flex flex-col items-center justify-center text-deep-500 dark:text-gray-400">
                                    <Activity className="w-12 h-12 mb-4 text-ocean-300" />
                                    <p>Monthly time series will appear after analysis</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeTab === 'length' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Length Distribution Chart */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Length-Frequency Distribution</CardTitle>
                            <CardDescription>Size distribution analysis for population assessment</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {lengthDistribution?.bins?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={lengthDistribution.bins}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                        <XAxis dataKey="lengthClass" stroke={chartColors.text} label={{ value: 'Length (cm)', position: 'bottom', fill: chartColors.text }} />
                                        <YAxis stroke={chartColors.text} label={{ value: 'Frequency', angle: -90, position: 'insideLeft', fill: chartColors.text }} />
                                        <Tooltip contentStyle={{ backgroundColor: chartColors.tooltipBg, color: chartColors.tooltipText, border: 'none', borderRadius: '12px' }} />
                                        <Bar dataKey="count" fill="#0891b2" name="Count" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-80 flex flex-col items-center justify-center text-deep-500 dark:text-gray-400">
                                    <BarChart2 className="w-12 h-12 mb-4 text-ocean-300" />
                                    <p>Run CPUE analysis to generate length distribution</p>
                                    <Button variant="outline" className="mt-4" onClick={runCPUEAnalysis}>
                                        Analyze Data
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Length Statistics */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Distribution Statistics</CardTitle>
                            <CardDescription>Key metrics from length frequency analysis</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {lengthDistribution ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-ocean-50 dark:bg-ocean-900/20 rounded-xl p-4">
                                            <p className="text-xs text-deep-500 dark:text-gray-400">Sample Size</p>
                                            <p className="text-2xl font-bold text-deep-900 dark:text-white">{lengthDistribution.sampleSize?.toLocaleString()}</p>
                                        </div>
                                        <div className="bg-marine-50 dark:bg-marine-900/20 rounded-xl p-4">
                                            <p className="text-xs text-deep-500 dark:text-gray-400">Mean Length</p>
                                            <p className="text-2xl font-bold text-deep-900 dark:text-white">{lengthDistribution.meanLength?.toFixed(1)} cm</p>
                                        </div>
                                        <div className="bg-coral-50 dark:bg-coral-900/20 rounded-xl p-4">
                                            <p className="text-xs text-deep-500 dark:text-gray-400">Min Length</p>
                                            <p className="text-2xl font-bold text-deep-900 dark:text-white">{lengthDistribution.minLength?.toFixed(1)} cm</p>
                                        </div>
                                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                                            <p className="text-xs text-deep-500 dark:text-gray-400">Max Length</p>
                                            <p className="text-2xl font-bold text-deep-900 dark:text-white">{lengthDistribution.maxLength?.toFixed(1)} cm</p>
                                        </div>
                                    </div>
                                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <div className="flex justify-between text-sm mb-2">
                                            <span className="text-deep-500 dark:text-gray-400">Mode</span>
                                            <span className="font-medium text-deep-900 dark:text-white">{lengthDistribution.mode?.toFixed(1)} cm</span>
                                        </div>
                                        <div className="flex justify-between text-sm mb-2">
                                            <span className="text-deep-500 dark:text-gray-400">Median</span>
                                            <span className="font-medium text-deep-900 dark:text-white">{lengthDistribution.medianLength?.toFixed(1)} cm</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-deep-500 dark:text-gray-400">Std Deviation</span>
                                            <span className="font-medium text-deep-900 dark:text-white">{lengthDistribution.standardDeviation?.toFixed(2)}</span>
                                        </div>
                                    </div>
                                    {growthParams && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                            <h4 className="font-medium text-deep-900 dark:text-white mb-2">Von Bertalanffy Growth</h4>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-deep-500 dark:text-gray-400">L∞ (asymptotic)</span>
                                                <span className="font-medium text-deep-900 dark:text-white">{growthParams.Linf?.toFixed(1)} cm</span>
                                            </div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span className="text-deep-500 dark:text-gray-400">K (growth rate)</span>
                                                <span className="font-medium text-deep-900 dark:text-white">{growthParams.K?.toFixed(3)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-deep-500 dark:text-gray-400">R² (fit)</span>
                                                <span className="font-medium text-deep-900 dark:text-white">{growthParams.r2?.toFixed(3)}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="h-60 flex flex-col items-center justify-center text-deep-500 dark:text-gray-400">
                                    <Activity className="w-10 h-10 mb-3 text-ocean-300" />
                                    <p className="text-sm">Upload length data and run analysis</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Maturity Ogive */}
                    <Card className="lg:col-span-2">
                        <CardHeader>
                            <CardTitle>Maturity Ogive</CardTitle>
                            <CardDescription>Percentage mature by length class (S-curve)</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {maturityOgive && maturityOgive.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={maturityOgive}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                        <XAxis
                                            dataKey="lengthClass"
                                            stroke={chartColors.text}
                                            label={{ value: 'Length (cm)', position: 'bottom', fill: chartColors.text }}
                                        />
                                        <YAxis
                                            stroke={chartColors.text}
                                            domain={[0, 100]}
                                            label={{ value: '% Mature', angle: -90, position: 'insideLeft', fill: chartColors.text }}
                                        />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: chartColors.tooltipBg, color: chartColors.tooltipText, border: 'none', borderRadius: '12px' }}
                                            formatter={(value: any, name: string) => {
                                                if (name === 'percentMature') return [`${value}%`, 'Mature'];
                                                return [value, name];
                                            }}
                                        />
                                        {/* Reference line at 50% */}
                                        <Line
                                            type="monotone"
                                            dataKey="percentMature"
                                            stroke="#8b5cf6"
                                            strokeWidth={3}
                                            dot={{ fill: '#8b5cf6' }}
                                            name="percentMature"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-60 flex flex-col items-center justify-center text-deep-500 dark:text-gray-400">
                                    <TrendingUp className="w-10 h-10 mb-3 text-purple-300" />
                                    <p className="text-sm">Maturity data requires records with maturity field</p>
                                    <p className="text-xs text-deep-400 dark:text-gray-500 mt-1">Values: immature, maturing, mature, spawning, spent</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Depth Distribution - Species distribution by depth */}
                    <Card className="lg:col-span-2">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <MapPin className="w-5 h-5 text-blue-500" />
                                Depth Distribution
                            </CardTitle>
                            <CardDescription>
                                Species catch distribution by depth range (25m bins)
                                {depthDistData?.summary?.peakDepth && (
                                    <span className="ml-2 text-blue-600 dark:text-blue-400 font-medium">
                                        • Peak: {depthDistData.summary.peakDepth.depthRange}
                                    </span>
                                )}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {depthDistData?.data && depthDistData.data.length > 0 ? (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={depthDistData.data} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                        <XAxis
                                            type="number"
                                            stroke={chartColors.text}
                                            label={{ value: 'Total Catch (kg)', position: 'bottom', fill: chartColors.text }}
                                        />
                                        <YAxis
                                            dataKey="depthRange"
                                            type="category"
                                            stroke={chartColors.text}
                                            width={80}
                                        />
                                        <Tooltip
                                            contentStyle={{ backgroundColor: chartColors.tooltipBg, color: chartColors.tooltipText, border: 'none', borderRadius: '12px' }}
                                            formatter={(value: any, name: string) => {
                                                if (name === 'totalCatch') return [`${value} kg`, 'Total Catch'];
                                                if (name === 'sampleCount') return [value, 'Samples'];
                                                return [value, name];
                                            }}
                                        />
                                        <Bar
                                            dataKey="totalCatch"
                                            fill="#3b82f6"
                                            radius={[0, 4, 4, 0]}
                                            name="totalCatch"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-60 flex flex-col items-center justify-center text-deep-500 dark:text-gray-400">
                                    <MapPin className="w-10 h-10 mb-3 text-blue-300" />
                                    <p className="text-sm">Depth distribution requires data with depth field</p>
                                    <p className="text-xs text-deep-400 dark:text-gray-500 mt-1">Re-run NOAA transform with enhanced columns</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeTab === 'stock' && (
                <div className="space-y-6">
                    {/* Sustainability Status Banner */}
                    {(stockStatus || stockMutation.data?.stockStatus) && (
                        <div className={cn(
                            "flex items-center justify-between p-4 rounded-xl border",
                            sustainabilityStatus === 'sustainable' && "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800",
                            sustainabilityStatus === 'fully_exploited' && "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800",
                            sustainabilityStatus === 'overfished' && "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800",
                            !sustainabilityStatus && "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                        )}>
                            <div className="flex items-center gap-3">
                                <div className={cn(
                                    "w-4 h-4 rounded-full",
                                    sustainabilityStatus === 'sustainable' && "bg-green-500",
                                    sustainabilityStatus === 'fully_exploited' && "bg-yellow-500",
                                    sustainabilityStatus === 'overfished' && "bg-red-500",
                                    !sustainabilityStatus && "bg-gray-400"
                                )} />
                                <div>
                                    <p className={cn(
                                        "font-semibold",
                                        sustainabilityStatus === 'sustainable' && "text-green-800 dark:text-green-200",
                                        sustainabilityStatus === 'fully_exploited' && "text-yellow-800 dark:text-yellow-200",
                                        sustainabilityStatus === 'overfished' && "text-red-800 dark:text-red-200",
                                        !sustainabilityStatus && "text-gray-700 dark:text-gray-300"
                                    )}>
                                        {sustainabilityStatus === 'sustainable' && '🟢 Sustainable'}
                                        {sustainabilityStatus === 'fully_exploited' && '🟡 Fully Exploited'}
                                        {sustainabilityStatus === 'overfished' && '🔴 Overfished'}
                                        {!sustainabilityStatus && 'Stock Status Unknown'}
                                    </p>
                                    <p className="text-sm text-deep-600 dark:text-gray-400">
                                        Score: {stockStatus?.sustainabilityScore?.toFixed(0) || '—'}/100
                                    </p>
                                </div>
                            </div>
                            {fmRatio !== null && (
                                <div className="text-right">
                                    <p className="text-sm text-deep-500 dark:text-gray-400">F/M Ratio</p>
                                    <p className={cn(
                                        "text-2xl font-bold",
                                        fmRatio < 1 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                                    )}>
                                        {fmRatio.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-deep-500 dark:text-gray-400">
                                        {fmRatio < 1 ? 'Below optimal' : 'Above optimal'}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Detailed Stock Assessment</CardTitle>
                                    <CardDescription>Comprehensive mortality and recruitment analysis</CardDescription>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate('/oceanography', {
                                        state: {
                                            parameter: 'fisheries_cpue',
                                            center: [43.0, -68.0],
                                            zoom: 6,
                                            mode: 'database'
                                        }
                                    })}
                                >
                                    <Fish className="w-4 h-4 mr-2 text-blue-500" />
                                    Analyze Spatial CPUE
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {(mortality || stockMutation.data?.success) ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Mortality */}
                                    <div className="p-6 bg-gray-50 dark:bg-deep-800 rounded-xl">
                                        <h3 className="text-lg font-semibold text-deep-900 dark:text-gray-100 mb-4">Mortality Rates</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-deep-600 dark:text-gray-400">Total (Z)</span>
                                                <span className="font-mono text-deep-900 dark:text-gray-100">{mortality?.totalMortality?.toFixed(3) || '—'}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-deep-600 dark:text-gray-400">Natural (M)</span>
                                                <span className="font-mono text-deep-900 dark:text-gray-100">{mortality?.naturalMortality?.toFixed(3) || mortality?.naturalMortalityEstimates?.pauly?.toFixed(3) || '—'}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-deep-600 dark:text-gray-400">Fishing (F)</span>
                                                <span className="font-mono text-deep-900 dark:text-gray-100">{mortality?.fishingMortality?.toFixed(3) || '—'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Exploitation */}
                                    <div className="p-6 bg-gray-50 dark:bg-deep-800 rounded-xl">
                                        <h3 className="text-lg font-semibold text-deep-900 dark:text-gray-100 mb-4">Exploitation</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-deep-600 dark:text-gray-400">Rate (E)</span>
                                                <span className="font-mono text-deep-900 dark:text-gray-100">{mortality?.exploitationRate?.toFixed(3) || '—'}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-deep-600 dark:text-gray-400">Optimal</span>
                                                <span className="font-mono text-deep-900 dark:text-gray-100">0.50</span>
                                            </div>
                                            <div className="mt-3">
                                                {mortality?.exploitationRate !== undefined && mortality?.exploitationRate !== null && mortality?.exploitationRate > 0 ? (
                                                    <Badge variant={mortality.exploitationRate < 0.5 ? 'success' : 'warning'}>
                                                        {mortality.exploitationRate < 0.5 ? 'Sustainable' : 'Overfished'}
                                                    </Badge>
                                                ) : sustainabilityStatus ? (
                                                    <Badge variant={sustainabilityStatus === 'sustainable' ? 'success' : sustainabilityStatus === 'overfished' ? 'destructive' : 'warning'}>
                                                        {sustainabilityStatus === 'sustainable' ? 'Sustainable' : sustainabilityStatus === 'overfished' ? 'Overfished' : 'Fully Exploited'}
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline">Unknown</Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Recruitment */}
                                    <div className="p-6 bg-gray-50 dark:bg-deep-800 rounded-xl">
                                        <h3 className="text-lg font-semibold text-deep-900 dark:text-gray-100 mb-4">Recruitment</h3>
                                        {recruitment ? (
                                            <div className="space-y-3">
                                                <div className="flex justify-between">
                                                    <span className="text-deep-600 dark:text-gray-400">Pattern</span>
                                                    <span className="font-mono text-deep-900 dark:text-gray-100">{recruitment.pattern}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-deep-600 dark:text-gray-400">Index</span>
                                                    <span className="font-mono text-deep-900 dark:text-gray-100">{(recruitment.index * 100).toFixed(1)}%</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-deep-500 dark:text-gray-400 text-sm">No recruitment data</p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="h-60 flex flex-col items-center justify-center text-deep-500 dark:text-gray-400">
                                    <Fish className="w-12 h-12 mb-4 text-ocean-300" />
                                    <p>Run stock assessment to see detailed analysis</p>
                                    <Button variant="outline" className="mt-4" onClick={runStockAssessment}>
                                        Assess Stock
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
